import { useState, useEffect, useCallback } from "react";
import { Shield, AlertTriangle, CheckCircle, Ban, Database, RefreshCw, Loader2, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const EVENT_CONFIG = {
  login_success: { label: "Login OK", color: "text-emerald-600", bg: "bg-emerald-50", icon: CheckCircle },
  login_failed: { label: "Login Fallito", color: "text-red-500", bg: "bg-red-50", icon: AlertTriangle },
  login_blocked: { label: "IP Bloccato", color: "text-red-700", bg: "bg-red-100", icon: Ban },
  login_inactive: { label: "Account Disattivato", color: "text-amber-600", bg: "bg-amber-50", icon: AlertTriangle },
};

export default function SecurityPage() {
  const [securityData, setSecurityData] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const fetchSecurity = useCallback(async () => {
    try {
      const params = filter ? { event: filter } : {};
      const res = await axios.get(`${API}/security/logs`, { params });
      setSecurityData(res.data);
    } catch { /* silent */ }
  }, [filter]);

  const fetchBackups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/backup/list`);
      setBackups(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchSecurity(), fetchBackups()]).finally(() => setLoading(false));
  }, [fetchSecurity, fetchBackups]);

  const runBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await axios.post(`${API}/backup/run`);
      toast.success(`Backup completato (${res.data.size_mb} MB)`);
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il backup");
    }
    setBackupLoading(false);
  };

  const unblockIp = async (ip) => {
    try {
      await axios.delete(`${API}/security/unblock/${ip}`);
      toast.success(`IP ${ip} sbloccato`);
      fetchSecurity();
    } catch {
      toast.error("Errore");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const stats = securityData?.stats || {};
  const activeBlocks = securityData?.active_blocks || {};

  return (
    <div data-testid="security-page" className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">
          Sicurezza & Backup
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
          Monitoraggio accessi, protezione brute-force e backup database
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-3 text-center">
          <div className="text-2xl font-black text-emerald-700">{stats.total_success || 0}</div>
          <div className="text-[10px] uppercase font-bold text-emerald-500">Login riusciti</div>
        </div>
        <div className="border border-red-200 bg-red-50/50 rounded-sm p-3 text-center">
          <div className="text-2xl font-black text-red-600">{stats.total_failed || 0}</div>
          <div className="text-[10px] uppercase font-bold text-red-400">Tentativi falliti</div>
        </div>
        <div className="border border-red-300 bg-red-100/50 rounded-sm p-3 text-center">
          <div className="text-2xl font-black text-red-700">{stats.total_blocked || 0}</div>
          <div className="text-[10px] uppercase font-bold text-red-500">IP bloccati</div>
        </div>
        <div className="border border-zinc-200 bg-zinc-50 rounded-sm p-3 text-center">
          <div className="text-2xl font-black text-zinc-700">{backups.length}</div>
          <div className="text-[10px] uppercase font-bold text-zinc-400">Backup salvati</div>
        </div>
      </div>

      {/* Active Blocks */}
      {Object.keys(activeBlocks).length > 0 && (
        <div className="border border-red-300 bg-red-50 rounded-sm p-4">
          <h3 className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-2">
            <Ban className="w-4 h-4" /> IP Attualmente Bloccati
          </h3>
          <div className="space-y-1.5">
            {Object.entries(activeBlocks).map(([ip, data]) => (
              <div key={ip} className="flex items-center justify-between bg-white rounded-sm px-3 py-2 border border-red-200">
                <div>
                  <span className="font-mono text-sm font-bold text-red-700">{ip}</span>
                  <span className="text-xs text-red-400 ml-2">
                    {data.count} tentativi · sblocco: {data.blocked_until ? formatDate(data.blocked_until) : "—"}
                  </span>
                </div>
                <button
                  data-testid={`unblock-${ip}`}
                  onClick={() => unblockIp(ip)}
                  className="text-xs text-red-600 hover:text-red-800 font-semibold flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Sblocca
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suspicious IPs */}
      {stats.suspicious_ips?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50/50 rounded-sm p-4">
          <h3 className="text-sm font-bold text-amber-700 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4" /> IP Sospetti (top 10)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {stats.suspicious_ips.map((s) => (
              <div key={s.ip} className="flex items-center justify-between bg-white rounded-sm px-3 py-1.5 border border-amber-200 text-xs">
                <span className="font-mono font-bold text-zinc-700">{s.ip}</span>
                <span className="text-amber-600 font-semibold">{s.attempts} tentativi · ultimo: {formatDate(s.last)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backup Section */}
      <div className="border border-zinc-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-1.5">
            <Database className="w-4 h-4" /> Backup Database
          </h3>
          <Button
            data-testid="run-backup-btn"
            onClick={runBackup}
            disabled={backupLoading}
            className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-8 px-3 text-xs font-semibold"
          >
            {backupLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Database className="w-3.5 h-3.5 mr-1.5" />}
            Esegui Backup
          </Button>
        </div>
        {backups.length === 0 ? (
          <p className="text-xs text-zinc-400">Nessun backup ancora. Backup automatico ogni notte alle 02:00.</p>
        ) : (
          <div className="space-y-1">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-zinc-50 rounded-sm px-3 py-2 text-xs border border-zinc-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="font-semibold text-zinc-700">{formatDate(b.timestamp)}</span>
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold ${b.triggered_by === "auto" ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"}`}>
                    {b.triggered_by === "auto" ? "AUTO" : "MANUALE"}
                  </span>
                </div>
                <span className="font-mono text-zinc-500">{b.size_mb} MB</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Log */}
      <div className="border border-zinc-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-1.5">
            <Shield className="w-4 h-4" /> Log Accessi
          </h3>
          <div className="flex items-center gap-1.5">
            <select
              data-testid="security-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white"
            >
              <option value="">Tutti</option>
              <option value="login_success">Login OK</option>
              <option value="login_failed">Falliti</option>
              <option value="login_blocked">Bloccati</option>
            </select>
            <button onClick={fetchSecurity} className="p-1 hover:bg-zinc-100 rounded-sm">
              <RefreshCw className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          </div>
        </div>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {(securityData?.logs || []).length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-4">Nessun evento registrato.</p>
          ) : (
            (securityData?.logs || []).map((log) => {
              const cfg = EVENT_CONFIG[log.event] || EVENT_CONFIG.login_failed;
              const Icon = cfg.icon;
              return (
                <div key={log.id} className={`flex items-start gap-2 rounded-sm px-3 py-2 border ${cfg.bg} border-zinc-100`}>
                  <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{log.ip}</span>
                      <span className="text-[10px] text-zinc-600 font-semibold">{log.email}</span>
                    </div>
                    {log.reason && <p className="text-[10px] text-zinc-400 mt-0.5">{log.reason}</p>}
                    <span className="text-[9px] text-zinc-400">{formatDate(log.timestamp)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
